import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import type { User } from "./Auth";
import type { UploadedFile } from "./FileUpload";

interface Import {
    id: number;
    createdAt: string;
    updatedAt: string;
    status: string;
    fileId: number;
    worksheetIndex: null | number;
    headerRowIndex: number;
    columnMapping: ColumnMapping[] | null;
    importOptions: ImportOption | null;
    validationResults: ValidationResult | null;
    results: Result | null;
    error: null | string;
    teamId: number;
    userId: number;
    user: User;
    file: UploadedFile;
    _links: Links;
}

interface Links {
    self: Self;
}

interface Self {
    href: string;
}

interface Result {
    errors: any[];
    startedAt: string;
    totalRows: number;
    failedRows: number;
    inProgress: boolean;
    completedAt: string;
    newContacts: number;
    filteredRows: number;
    processedRows: number;
    successfulRows: number;
    updatedContacts: number;
    newVendorRelationships: number;
    updatedVendorRelationships: number;
}

interface ValidationResult {
    totalRows: number;
    validRows: number;
    invalidRows: number;
    filteredRows: number;
    sampleValidations: SampleValidation[];
    potentialNewVendors: number;
    potentialNewContacts: number;
    potentialExistingVendors: number;
    potentialExistingContacts: number;
}

interface SampleValidation {
    issues: any[];
    isValid: boolean;
    rowIndex: number;
    mappedData: MappedData;
    sourceData: SourceData;
    vendorMatches: VendorMatch[];
    contactMatches: ContactMatch[];
}

interface ContactMatch {
    id: number;
    email: string;
    title: null;
    lastName: null;
    createdAt: string;
    createdBy: number;
    deletedAt: null;
    firstName: null;
    updatedAt: string;
    vendorRelationship: VendorMatch;
    vendorRelationshipId: number;
}

interface VendorMatch {
    id: number;
    name: string;
    domain: null;
    teamId: number;
    createdAt: string;
    deletedAt: null;
    updatedAt: string;
    vendorPublicId: null;
}

interface SourceData {
    email: string;
    phone: string;
    category: string;
    isActive: string;
    vendorName: string;
    contactName: string;
    erpVendorld: string;
    accountNumber: string;
    erpVendorCode: string;
}

interface MappedData {
    email: string;
    category: string;
    isActive: string;
    firstName: string;
    vendorName: string;
    erpVendorId: string;
    accountNumber: string;
    erpVendorCode: string;
}

interface ImportOption {
    skipInvalidRows: boolean;
    updateExistingContacts: boolean;
    mergeVendorsWithSameDomain: boolean;
}

interface ColumnMapping {
    name: string;
    index: number;
    validation: Validation;
    mappedField: string;
    sampleValues: string[];
    suggestedMapping: null | string;
}

interface Validation {
    isValid: boolean;
    errors?: string[];
}

export default {
    createImport: (data: { fileId: number }) =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/imports`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(data),
        }).then(res => res.json()),

    getImport: (importId: number | string) =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/imports/${importId}`, {
            headers: getHeaders(),
        }).then(res => res.json()),

    analyzeImport: (importId: number | string) =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/imports/${importId}/analyze`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => res.json()),

    setColumnMapping: (importId: number | string, mapping: object) =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/imports/${importId}/mapping`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(mapping),
        }).then(res => res.json()),

    validateMapping: (importId: number | string) =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/imports/${importId}/validate`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => res.json()),

    executeImport: (importId: number | string, options?: { rowFilter?: { type: string; excludedRows: number[] }; dryRun: boolean }) =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/imports/${importId}/execute`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(options),
        }).then(res => res.json()),

    getMappingOptions: () =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/imports/mappable-fields`, {
            headers: getHeaders(),
        }).then(res => res.json()),

    getImports: (): Promise<{
        imports: Import[];
        limit: number;
        offset: number;
        total: number;
        _links: Links;
    }> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/imports`, {
            headers: getHeaders(),
        }).then(res => res.json()),
};
